"use client";

import { useTranslations } from "next-intl";
import { useState } from "react";
import { toast } from "sonner";
import type { CreateReviewData } from "@/features/reviews/shared/types";
import { LikesEnum } from "@/lib/shared/types/LikesEnum";
import { Button, Checkbox, HStack, Input, Label, Textarea, VStack } from "@/ui";
import { useSubmitReview } from "../hooks/useSubmitReview";
import { StarRatingInput } from "./StarRatingInput";

interface ReviewFormProps {
  productId: string;
  onSuccess?: () => void;
}

export function ReviewForm({ productId, onSuccess }: ReviewFormProps) {
  const reviewsT = useTranslations("reviews");
  const generalT = useTranslations("general");
  const toastT = useTranslations("toast");
  const validationT = useTranslations("validation");

  const [rating, setRating] = useState(0);
  const [content, setContent] = useState("");
  const [author, setAuthor] = useState("");
  const [selectedLikes, setSelectedLikes] = useState<LikesEnum[]>([]);

  const submitMutation = useSubmitReview();

  const likeOptions: { value: LikesEnum; label: string }[] = [
    { value: LikesEnum.DESIGN, label: reviewsT("design") },
    { value: LikesEnum.MATERIAL, label: reviewsT("material") },
    { value: LikesEnum.PRICE, label: reviewsT("price") },
  ];

  const handleLikeToggle = (like: LikesEnum) => {
    setSelectedLikes((prev) =>
      prev.includes(like) ? prev.filter((l) => l !== like) : [...prev, like],
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (rating === 0) {
      toast.error(toastT("error.title"), {
        description: validationT("required", { field: reviewsT("rating") }),
      });
      return;
    }

    if (!content.trim()) {
      toast.error(toastT("error.title"), {
        description: validationT("required", { field: reviewsT("free_text") }),
      });
      return;
    }

    if (!author.trim()) {
      toast.error(toastT("error.title"), {
        description: validationT("required", { field: reviewsT("name") }),
      });
      return;
    }

    const data: CreateReviewData = {
      productId,
      rating,
      content: content.trim(),
      author: author.trim(),
      likes: selectedLikes,
    };

    try {
      await submitMutation.mutateAsync(data);
      toast.success(toastT("success.title"), {
        description: toastT("success.review"),
      });

      // Reset form
      setRating(0);
      setContent("");
      setAuthor("");
      setSelectedLikes([]);

      onSuccess?.();
    } catch (error) {
      const errorCode = error instanceof Error ? error.message : "FAILED_TO_SUBMIT";
      toast.error(toastT("error.title"), {
        description:
          errorCode === "ALREADY_REVIEWED" ? reviewsT("already_reviewed") : toastT("error.message"),
      });
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="w-full"
    >
      <VStack className="gap-6">
        {/* Rating */}
        <VStack className="gap-2">
          <Label>{reviewsT("rating")}</Label>
          <StarRatingInput
            value={rating}
            onChange={setRating}
            disabled={submitMutation.isPending}
          />
        </VStack>

        {/* Author name */}
        <VStack className="gap-2">
          <Label htmlFor="author">{reviewsT("name")}</Label>
          <Input
            id="author"
            value={author}
            onChange={(e) => setAuthor(e.target.value)}
            placeholder={reviewsT("name")}
            disabled={submitMutation.isPending}
          />
        </VStack>

        {/* What did they like */}
        <VStack className="gap-2">
          <Label>{reviewsT("goods")}</Label>
          <HStack className="gap-4 flex-wrap">
            {likeOptions.map((option) => (
              <HStack
                key={option.value}
                className="items-center gap-2"
              >
                <Checkbox
                  id={`like-${option.value}`}
                  checked={selectedLikes.includes(option.value)}
                  onCheckedChange={() => handleLikeToggle(option.value)}
                  disabled={submitMutation.isPending}
                />
                <Label
                  htmlFor={`like-${option.value}`}
                  className="cursor-pointer font-normal"
                >
                  {option.label}
                </Label>
              </HStack>
            ))}
          </HStack>
        </VStack>

        {/* Review content */}
        <VStack className="gap-2">
          <Label htmlFor="content">{reviewsT("free_text")}</Label>
          <Textarea
            id="content"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={reviewsT("share_your_experience")}
            rows={4}
            disabled={submitMutation.isPending}
          />
        </VStack>

        {/* Submit button */}
        <Button
          type="submit"
          disabled={submitMutation.isPending || rating === 0 || !content.trim()}
          className="self-start"
        >
          {submitMutation.isPending ? generalT("loading") : generalT("submit")}
        </Button>
      </VStack>
    </form>
  );
}
